import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Camera, User, Heart, MapPin, ChevronRight, ChevronLeft, 
  Check, Loader2, Upload, X, Sparkles, Navigation
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { useAuth, API } from '../App';
import axios from 'axios';
import { toast } from 'sonner';

const AVAILABLE_INTERESTS = [
  'Rejser', 'Musik', 'Film', 'Sport', 'Fitness', 'Madlavning', 
  'Kunst', 'Fotografering', 'Gaming', 'Læsning', 'Natur', 'Dyr',
  'Mode', 'Teknologi', 'Yoga', 'Dans', 'Kaffe', 'Vin',
  'Vandreture', 'Strand', 'Koncerter', 'Teater', 'Comedy', 'Podcasts'
];

const Onboarding = () => {
  const { user, token, refreshUser } = useAuth();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  
  // Form data
  const [photos, setPhotos] = useState(user?.photos || []);
  const [bio, setBio] = useState(user?.bio || '');
  const [age, setAge] = useState(user?.age || '');
  const [gender, setGender] = useState(user?.gender || '');
  const [lookingFor, setLookingFor] = useState(user?.looking_for || '');
  const [location, setLocation] = useState(user?.location || '');
  const [interests, setInterests] = useState(user?.interests || []);
  const [gettingLocation, setGettingLocation] = useState(false);
  const [latitude, setLatitude] = useState(null);
  const [longitude, setLongitude] = useState(null);

  const totalSteps = 4;

  // Get user's location using GPS
  const getGPSLocation = async () => {
    if (!navigator.geolocation) {
      toast.error('Din browser understøtter ikke GPS');
      return;
    }

    setGettingLocation(true);
    
    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const { latitude, longitude } = position.coords;
        setLatitude(latitude);
        setLongitude(longitude);
        
        // Try to get city name from coordinates using reverse geocoding
        try {
          const response = await fetch(
            `https://nominatim.openstreetmap.org/reverse?format=json&lat=${latitude}&lon=${longitude}&accept-language=da`
          );
          const data = await response.json();
          
          const city = data.address?.city || 
                       data.address?.town || 
                       data.address?.village || 
                       data.address?.municipality ||
                       data.address?.county;
          
          if (city) {
            setLocation(city);
            toast.success(`Lokation fundet: ${city}`);
          } else {
            setLocation(`${latitude.toFixed(2)}, ${longitude.toFixed(2)}`);
            toast.success('Lokation fundet!');
          }
        } catch (err) {
          // Fallback to coordinates if geocoding fails
          setLocation(`${latitude.toFixed(2)}, ${longitude.toFixed(2)}`);
          toast.success('Lokation fundet!');
        }
        
        setGettingLocation(false);
      },
      (error) => {
        setGettingLocation(false);
        switch (error.code) {
          case error.PERMISSION_DENIED:
            toast.error('Du skal give tilladelse til lokation');
            break;
          case error.POSITION_UNAVAILABLE:
            toast.error('Lokation ikke tilgængelig');
            break;
          case error.TIMEOUT:
            toast.error('Timeout ved hentning af lokation');
            break;
          default:
            toast.error('Kunne ikke hente lokation');
        }
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 60000 }
    );
  };

  const handlePhotoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (photos.length >= 6) {
      toast.error('Maksimalt 6 billeder tilladt');
      return;
    }

    setUploadingPhoto(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const res = await axios.post(`${API}/upload/photo`, formData, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      setPhotos([...photos, res.data.url]);
      toast.success('Billede uploadet!');
    } catch (err) {
      toast.error('Kunne ikke uploade billede');
    } finally {
      setUploadingPhoto(false);
    }
  };

  const removePhoto = (index) => {
    setPhotos(photos.filter((_, i) => i !== index));
  };

  const toggleInterest = (interest) => {
    if (interests.includes(interest)) {
      setInterests(interests.filter(i => i !== interest));
    } else if (interests.length < 10) {
      setInterests([...interests, interest]);
    } else {
      toast.error('Maksimalt 10 interesser');
    }
  };

  const canProceed = () => {
    switch (step) {
      case 1: return photos.length >= 1;
      case 2: return bio.length >= 10 && age >= 18;
      case 3: return gender && lookingFor;
      case 4: return interests.length >= 3;
      default: return false;
    }
  };

  const handleNext = () => {
    if (step < totalSteps) {
      setStep(step + 1);
    } else {
      handleComplete();
    }
  };

  const handleComplete = async () => {
    setLoading(true);
    try {
      // Save profile data including coordinates if available
      const profileData = {
        photos,
        bio,
        age: parseInt(age),
        gender,
        looking_for: lookingFor,
        location,
        interests
      };
      
      // Add coordinates if we have them from GPS
      if (latitude && longitude) {
        profileData.latitude = latitude;
        profileData.longitude = longitude;
      }
      
      await axios.put(`${API}/profile`, profileData, {
        headers: { Authorization: `Bearer ${token}` }
      });

      // Mark onboarding as complete
      await axios.post(`${API}/onboarding/complete`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });

      await refreshUser();
      toast.success('Profil oprettet! Velkommen til Flirra!');
      navigate('/discover');
    } catch (err) {
      toast.error('Kunne ikke gemme profil');
    } finally {
      setLoading(false);
    }
  };

  const renderStep = () => {
    switch (step) {
      case 1:
        return (
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            className="space-y-6"
          >
            <div className="text-center mb-6">
              <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4">
                <Camera className="w-8 h-8 text-primary" />
              </div>
              <h2 className="text-2xl font-bold">Tilføj billeder</h2>
              <p className="text-muted-foreground mt-2">
                Tilføj mindst 1 billede så andre kan se dig
              </p>
            </div>

            <div className="grid grid-cols-3 gap-3">
              {[0, 1, 2, 3, 4, 5].map((index) => (
                <div
                  key={index}
                  className={`aspect-square rounded-xl border-2 border-dashed ${
                    photos[index] ? 'border-primary' : 'border-border'
                  } flex items-center justify-center relative overflow-hidden bg-muted/30`}
                >
                  {photos[index] ? (
                    <>
                      <img
                        src={photos[index]}
                        alt={`Foto ${index + 1}`}
                        className="w-full h-full object-cover"
                      />
                      <button
                        onClick={() => removePhoto(index)}
                        className="absolute top-1 right-1 w-6 h-6 bg-red-500 rounded-full flex items-center justify-center"
                      >
                        <X className="w-4 h-4 text-white" />
                      </button>
                      {index === 0 && (
                        <span className="absolute bottom-1 left-1 text-xs bg-primary text-white px-2 py-0.5 rounded-full">
                          Hovedbillede
                        </span>
                      )}
                    </>
                  ) : (
                    <button
                      onClick={() => fileInputRef.current?.click()}
                      disabled={uploadingPhoto}
                      className="w-full h-full flex flex-col items-center justify-center text-muted-foreground hover:text-primary transition-colors"
                    >
                      {uploadingPhoto ? (
                        <Loader2 className="w-6 h-6 animate-spin" />
                      ) : (
                        <>
                          <Upload className="w-6 h-6 mb-1" />
                          <span className="text-xs">Tilføj</span>
                        </>
                      )}
                    </button>
                  )}
                </div>
              ))}
            </div>

            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handlePhotoUpload}
              className="hidden"
            />

            <p className="text-center text-sm text-muted-foreground">
              {photos.length}/6 billeder • Minimum 1 påkrævet
            </p>
          </motion.div>
        );

      case 2:
        return (
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            className="space-y-6"
          >
            <div className="text-center mb-6">
              <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4">
                <User className="w-8 h-8 text-primary" />
              </div>
              <h2 className="text-2xl font-bold">Om dig</h2>
              <p className="text-muted-foreground mt-2">
                Fortæl lidt om dig selv
              </p>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2">Din alder</label>
                <Input
                  type="number"
                  value={age}
                  onChange={(e) => setAge(e.target.value)}
                  placeholder="Din alder"
                  min={18}
                  max={100}
                  className="text-lg"
                />
                {age && age < 18 && (
                  <p className="text-red-500 text-sm mt-1">Du skal være mindst 18 år</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">By/Område</label>
                <div className="flex gap-2">
                  <Input
                    type="text"
                    value={location}
                    onChange={(e) => setLocation(e.target.value)}
                    placeholder="F.eks. København, Aarhus..."
                    className="text-lg flex-1"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    onClick={getGPSLocation}
                    disabled={gettingLocation}
                    className="flex-shrink-0"
                  >
                    {gettingLocation ? (
                      <Loader2 className="w-5 h-5 animate-spin" />
                    ) : (
                      <Navigation className="w-5 h-5" />
                    )}
                  </Button>
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                  Skriv din by eller tryk på GPS-ikonet
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">
                  Bio ({bio.length}/500)
                </label>
                <textarea
                  value={bio}
                  onChange={(e) => setBio(e.target.value.slice(0, 500))}
                  placeholder="Skriv lidt om dig selv, dine interesser og hvad du søger..."
                  rows={4}
                  className="w-full px-4 py-3 rounded-xl border border-border bg-background text-foreground resize-none focus:outline-none focus:ring-2 focus:ring-primary"
                />
                {bio.length < 10 && (
                  <p className="text-muted-foreground text-sm mt-1">Minimum 10 tegn</p>
                )}
              </div>
            </div>
          </motion.div>
        );

      case 3:
        return (
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            className="space-y-6"
          >
            <div className="text-center mb-6">
              <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4">
                <Heart className="w-8 h-8 text-primary" />
              </div>
              <h2 className="text-2xl font-bold">Hvad søger du?</h2>
              <p className="text-muted-foreground mt-2">
                Fortæl os hvem du er og hvem du leder efter
              </p>
            </div>

            <div className="space-y-6">
              <div>
                <label className="block text-sm font-medium mb-3">Jeg er</label>
                <div className="grid grid-cols-3 gap-3">
                  {[
                    { value: 'male', label: 'Mand' },
                    { value: 'female', label: 'Kvinde' },
                    { value: 'other', label: 'Andet' }
                  ].map((option) => (
                    <button
                      key={option.value}
                      onClick={() => setGender(option.value)}
                      className={`py-3 px-4 rounded-xl border-2 transition-all ${
                        gender === option.value
                          ? 'border-primary bg-primary/10 text-primary'
                          : 'border-border hover:border-primary/50'
                      }`}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium mb-3">Jeg søger</label>
                <div className="grid grid-cols-3 gap-3">
                  {[
                    { value: 'male', label: 'Mænd' },
                    { value: 'female', label: 'Kvinder' },
                    { value: 'both', label: 'Begge' }
                  ].map((option) => (
                    <button
                      key={option.value}
                      onClick={() => setLookingFor(option.value)}
                      className={`py-3 px-4 rounded-xl border-2 transition-all ${
                        lookingFor === option.value
                          ? 'border-primary bg-primary/10 text-primary'
                          : 'border-border hover:border-primary/50'
                      }`}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </motion.div>
        );

      case 4:
        return (
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            className="space-y-6"
          >
            <div className="text-center mb-6">
              <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4">
                <Sparkles className="w-8 h-8 text-primary" />
              </div>
              <h2 className="text-2xl font-bold">Dine interesser</h2>
              <p className="text-muted-foreground mt-2">
                Vælg mindst 3 interesser (maks 10)
              </p>
            </div>

            <div className="flex flex-wrap gap-2">
              {AVAILABLE_INTERESTS.map((interest) => (
                <button
                  key={interest}
                  onClick={() => toggleInterest(interest)}
                  className={`px-4 py-2 rounded-full border transition-all ${
                    interests.includes(interest)
                      ? 'border-primary bg-primary text-white'
                      : 'border-border hover:border-primary/50'
                  }`}
                >
                  {interest}
                </button>
              ))}
            </div>

            <p className="text-center text-sm text-muted-foreground">
              {interests.length}/10 interesser valgt • Minimum 3 påkrævet
            </p>
          </motion.div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-background flex flex-col">
      {/* Progress bar */}
      <div className="sticky top-0 bg-background/80 backdrop-blur-sm z-10 px-4 py-3 border-b border-border">
        <div className="max-w-lg mx-auto">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-muted-foreground">Trin {step} af {totalSteps}</span>
            <span className="text-sm font-medium">{Math.round((step / totalSteps) * 100)}%</span>
          </div>
          <div className="h-2 bg-muted rounded-full overflow-hidden">
            <motion.div
              className="h-full bg-primary"
              initial={{ width: 0 }}
              animate={{ width: `${(step / totalSteps) * 100}%` }}
              transition={{ duration: 0.3 }}
            />
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 px-4 py-6 max-w-lg mx-auto w-full">
        <AnimatePresence mode="wait">
          {renderStep()}
        </AnimatePresence>
      </div>

      {/* Navigation */}
      <div className="sticky bottom-0 bg-background/80 backdrop-blur-sm border-t border-border px-4 py-4">
        <div className="max-w-lg mx-auto flex gap-3">
          {step > 1 && (
            <Button
              variant="outline"
              onClick={() => setStep(step - 1)}
              className="flex-1 rounded-full"
            >
              <ChevronLeft className="w-4 h-4 mr-2" />
              Tilbage
            </Button>
          )}
          
          <Button
            onClick={handleNext}
            disabled={!canProceed() || loading}
            className="flex-1 rounded-full bg-primary hover:bg-primary/90"
          >
            {loading ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : step === totalSteps ? (
              <>
                <Check className="w-4 h-4 mr-2" />
                Færdig!
              </>
            ) : (
              <>
                Næste
                <ChevronRight className="w-4 h-4 ml-2" />
              </>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default Onboarding;
